import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  Dropdown,
  Input,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Option,
  Textarea,
} from '@fluentui/react-components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  Cloud,
  Network,
  Server,
} from 'lucide-react';

import { ApiError } from '../api/client';
import {
  CreateMigrationRequest,
  DESTINATION_TENANT_REQUIRED,
  MIGRATION_WAVES,
  MigrationWave,
  addDevices,
  createMigration,
  createMigrationErrorCode,
} from '../api/migrations';
import {
  createMigrationGroup,
  getMigrationGroups,
  updateMigrationGroup,
} from '../api/migrationGroups';
import RouteFlight from '../components/RouteFlight';
import TenantReadinessPanel from '../components/tenant/TenantReadinessPanel';
import TenantResolveField from '../components/TenantResolveField';
import { EnvironmentContextValue, useOptionalEnvironment } from '../context/EnvironmentContext';
import { TenantResolveState, useTenantResolve } from '../hooks/useTenantResolve';
import {
  MigrationTypeLabel,
  migrationTypeDescription,
  migrationTypeWire,
} from '../lib/vocabulary';
import { ACCENT, PLAIN_BUTTON, STATUS, STROKE, SURFACE, TEXT } from '../theme/tokens';

// FD-282 (FD-ADR-031 §3/§4) — the onboarding wizard, replacing the single-form
// CreateMigrationPage.
//
// It asks what an operator actually knows — "where do these devices live
// today?" — instead of offering `D2C` / `T2T`. The answer maps to the wire value
// through `lib/vocabulary`, and NOTHING else in this file names a wire value
// except the one submit line. Two of the three device situations (domain-joined
// and hybrid-joined) are the same wire type, which is exactly why the question
// is worth asking in these terms: an operator with hybrid-joined PCs reading a
// "Domain to Entra ID (D2C)" radio has to work out that it applies to them.
//
// THREE THINGS THE PROTOTYPE COULD DO THAT PRODUCTION CANNOT, each found by
// reading the real API rather than by porting the mock:
//
// 1. **Source-tenant device groups cannot be browsed before creation.**
//    `searchSourceGroups(migrationId, …)` is migration-scoped — the server
//    resolves the source tenant FROM the migration record, so there is no
//    migration to scope by until this wizard has finished. The prototype's
//    group browser ran on `PROTO_ENTRA_GROUPS`, a mock with no such constraint.
//    The optional add-devices step therefore takes pasted serials only and says
//    plainly where group browsing lives.
//
// 2. **The console does not know the deployment tenant's domain.**
//    `DEPLOYMENT_TENANT_ID` is a Function App app setting with no read endpoint;
//    the prototype hardcoded `contoso.com`. So the default destination is named
//    by what it IS ("this deployment's own Entra tenant") and never by a domain
//    the console would be guessing.
//
// 3. **Tenants are entered as a domain OR a raw GUID**, resolved through the
//    real FD-110 `useTenantResolve` debounce, not typed as a bare string. That
//    is what makes `TenantReadinessPanel`'s consent probe possible at all.

type DeviceHome = 'domain' | 'hybrid' | 'entra';

/**
 * Where a HYBRID campaign's destination comes from (FD-594).
 *
 * - `deployment-default` — the deployment's own Entra tenant is offered as the
 *   default, with an optional override. Self-hosted (single-tenant): that tenant
 *   is the customer's own.
 * - `explicit-hosted` — this environment is Rubix-hosted (multi-tenant). Its
 *   deployment tenant is RUBIX'S, so "the default" would join a customer's
 *   devices to Rubix's tenant past the point of no return. No default is offered.
 * - `explicit-unconfirmed` — the hosting mode is not known (probe in flight,
 *   probe failed, or a Manager that does not report it). No default is offered
 *   here either; see below.
 *
 * WHY UNKNOWN DOES NOT FALL BACK TO THE DEFAULT. The two wrong answers are not
 * symmetric. Offering the default on a hosted environment points a customer's
 * devices at the wrong organisation; withholding it on a self-hosted one costs
 * the operator typing their own domain, which resolves like any other
 * destination. That is a slower path, never a blocked one. The window is also
 * small: the boot probe has usually answered long before step 3, and the
 * moment it reports `single-tenant` the default returns.
 *
 * PRESENTATION ONLY. The server refuses a hosted Hybrid campaign with no
 * destination (`400 DESTINATION_TENANT_REQUIRED`); this only stops the console
 * offering what would be refused.
 */
export type HybridDestinationPolicy =
  | 'deployment-default'
  | 'explicit-hosted'
  | 'explicit-unconfirmed';

export function hybridDestinationPolicy(
  env: Pick<EnvironmentContextValue, 'activeHealth'> | null,
): HybridDestinationPolicy {
  // Outside an EnvironmentProvider. Unreachable in the app — `api/client.ts`
  // throws on any request made without one, so the wizard could not create
  // anything — and it is the shape this page's own unit tests mount in. Today's
  // behaviour is kept for it rather than wrapping every existing test.
  if (!env) return 'deployment-default';
  const mode = env.activeHealth.adminAuthMode?.trim().toLowerCase() ?? null;
  if (mode === 'single-tenant') return 'deployment-default';
  if (mode === 'multi-tenant') return 'explicit-hosted';
  return 'explicit-unconfirmed';
}

const STEPS = [
  'Devices today',
  'Name & stage',
  'Route',
  'Organize',
  'Add devices',
  'Review',
] as const;

const DEVICE_HOME_OPTIONS: Array<{
  value: DeviceHome;
  icon: ReactNode;
  title: string;
  detail: string;
}> = [
  {
    value: 'domain',
    icon: <Server size={20} strokeWidth={1.5} />,
    title: 'Joined to on-premises Active Directory',
    detail: 'Classic domain-joined PCs managed by AD and Group Policy',
  },
  {
    value: 'hybrid',
    icon: <Network size={20} strokeWidth={1.5} />,
    title: 'Joined to Active Directory and Microsoft Entra',
    detail: 'Hybrid-joined PCs — registered in Entra but still bound to the domain',
  },
  {
    value: 'entra',
    icon: <Cloud size={20} strokeWidth={1.5} />,
    title: 'Joined to Microsoft Entra ID in another organization',
    detail: 'Cloud-only PCs moving from a different Entra tenant',
  },
];

/**
 * Domain-joined and hybrid-joined are BOTH the Hybrid path; only
 * Entra→Entra is Cloud. Kept as a named function rather than an inline ternary
 * because the collapse of three answers into two types is the non-obvious part.
 */
function labelForDeviceHome(home: DeviceHome): MigrationTypeLabel {
  return home === 'entra' ? 'Cloud' : 'Hybrid';
}

/**
 * "Boston Pilot" → "Boston Rollout". Strips a trailing stage word so the
 * suggested GROUP name reads as the campaign family rather than as one phase of
 * it — a group holding a campaign called "Boston Pilot" should not itself be
 * called "Boston Pilot".
 */
export function suggestGroupName(campaignName: string): string {
  const cleaned = campaignName
    .trim()
    .replace(/\b(pilot|production|wave|ring|batch|phase)\b\s*\d*\s*$/i, '')
    .replace(/[\s\-–—·]+$/, '')
    .trim();
  return cleaned.length > 0 ? `${cleaned} Rollout` : 'New rollout group';
}

/** Serials separated by whitespace, commas, or semicolons. Deduped. */
export function parseSerials(text: string): string[] {
  return [...new Set(text.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean))];
}

export default function NewMigrationWizard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [step, setStep] = useState(0);
  const [deviceHome, setDeviceHome] = useState<DeviceHome | null>(null);
  const [name, setName] = useState('');
  const [wave, setWave] = useState<MigrationWave>('Pilot');
  const [sourceInput, setSourceInput] = useState('');
  const [destInput, setDestInput] = useState('');
  const [destOverridden, setDestOverridden] = useState(false);
  const [groupChoice, setGroupChoice] = useState<'new' | string>('new');
  const [newGroupName, setNewGroupName] = useState('');
  const [groupNameTouched, setGroupNameTouched] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [autopilotTag, setAutopilotTag] = useState('');
  const [serialsText, setSerialsText] = useState('');
  // Set when the campaign was created but a LATER step failed. The campaign
  // exists at that point, so the wizard must stop offering to create it again.
  const [createdMigrationId, setCreatedMigrationId] = useState<string | null>(null);
  // FD-598 — the group THIS wizard session already created, if a later link of
  // the create chain then failed. The group is written before the campaign (so
  // the campaign is filed on creation), which means a refused campaign create
  // leaves it behind; a retry must reuse it rather than create a second one.
  // Before this, the retry either produced a same-named duplicate or — since the
  // name check usually sees the first — a 409 GROUP_NAME_CONFLICT the operator
  // could only escape by renaming. Component state, never localStorage: it
  // belongs to this wizard session and to nothing that outlives it.
  const [createdGroup, setCreatedGroup] = useState<{ id: string; name: string } | null>(null);

  const typeLabel = deviceHome ? labelForDeviceHome(deviceHome) : null;
  const isCloud = typeLabel === 'Cloud';

  const destinationPolicy = hybridDestinationPolicy(useOptionalEnvironment());
  // A Hybrid campaign on an environment where the deployment tenant is not a
  // safe default (FD-594). The destination field is then the ordinary path, not
  // an override behind a disclosure.
  const hybridDestRequired = !isCloud && destinationPolicy !== 'deployment-default';

  // Real FD-110 resolution — a domain debounces to a GUID, a raw GUID
  // short-circuits with no network call. The SOURCE slot is Cloud-only: a
  // Hybrid campaign's source identities are on-prem AD users and the server
  // rejects a source tenant outright (400 D2C_HAS_NO_SOURCE_TENANT).
  const sourceState = useTenantResolve(sourceInput, isCloud);
  const destState = useTenantResolve(destInput, true);
  const destProvided = destInput.trim().length > 0;

  const groupsQuery = useQuery({
    queryKey: ['migration-groups'],
    queryFn: getMigrationGroups,
  });
  const groups = groupsQuery.data?.groups ?? [];

  // The suggested group name tracks the campaign name until the operator edits
  // it themselves — after that it is theirs and is never overwritten.
  useEffect(() => {
    if (!groupNameTouched) setNewGroupName(suggestGroupName(name));
  }, [name, groupNameTouched]);

  const serials = useMemo(() => parseSerials(serialsText), [serialsText]);

  const sourceTenantId = isCloud ? settledTenantId(sourceState) : null;
  const destTenantId =
    isCloud || hybridDestRequired || destProvided ? settledTenantId(destState) : null;

  const selectedGroup = groups.find((g) => g.migrationGroupId === groupChoice) ?? null;

  // Cloud needs both slots settled. Hybrid needs nothing where the deployment
  // tenant is a safe default — but a typed-but-unsettled override must not pass,
  // because submitting mid-resolve would send no destination and silently create
  // a deployment-tenant campaign the operator did not ask for. Where it is NOT a
  // safe default (FD-594) the destination is required outright.
  //
  // Held outside `stepValid` so the CREATE button re-checks it too: the hosting
  // mode can arrive after the operator has already left step 3.
  const routeValid = isCloud
    ? tenantSlotReady(sourceState) && tenantSlotReady(destState)
    : hybridDestRequired
      ? tenantSlotReady(destState)
      : !destProvided || tenantSlotReady(destState);

  const stepValid = (() => {
    switch (step) {
      case 0:
        return deviceHome !== null;
      case 1:
        return name.trim().length > 0;
      case 2:
        return routeValid;
      case 3:
        return groupChoice !== 'new' || newGroupName.trim().length > 0;
      default:
        return true;
    }
  })();

  // Create is a CHAIN, and each link can fail after the previous one committed.
  // No endpoint does group-create + migration-create + device-assign in one
  // call, so the mutation reports exactly how far it got rather than presenting
  // a partial result as a failure.
  const mutation = useMutation({
    mutationFn: async () => {
      const wire = migrationTypeWire(typeLabel);
      if (!wire) throw new Error('No migration type selected');

      let migrationGroupId: string | undefined =
        groupChoice === 'new' ? undefined : groupChoice;
      if (groupChoice === 'new') {
        const wantedName = newGroupName.trim();
        if (createdGroup) {
          // Retry after a refused create: reuse the group this session made. If
          // the operator renamed it in the meantime, rename the existing group
          // rather than create another and leave the first one empty.
          if (createdGroup.name !== wantedName) {
            await updateMigrationGroup(createdGroup.id, { name: wantedName });
            setCreatedGroup({ id: createdGroup.id, name: wantedName });
          }
          migrationGroupId = createdGroup.id;
        } else {
          const group = await createMigrationGroup({ name: wantedName });
          // Recorded before the campaign create can throw, for the same reason
          // createdMigrationId is recorded below.
          setCreatedGroup({ id: group.migrationGroupId, name: wantedName });
          migrationGroupId = group.migrationGroupId;
        }
      }

      const body: CreateMigrationRequest = {
        migrationName: name.trim(),
        migrationWave: wave,
        migrationType: wire,
      };
      if (migrationGroupId) body.migrationGroupId = migrationGroupId;
      if (isCloud) {
        const source = tenantSubmitValues(sourceState);
        const dest = tenantSubmitValues(destState);
        body.sourceTenantId = source.tenantId;
        body.destTenantId = dest.tenantId;
        if (source.domain) body.sourceTenantDomain = source.domain;
        if (dest.domain) body.destTenantDomain = dest.domain;
      } else if (hybridDestRequired || destProvided) {
        // FD-ADR-029 — optional on Hybrid. Left blank, NOTHING is sent, and the
        // request is wire-identical to a campaign created before the override
        // existed. That is what keeps the zero-config path free of the
        // override's onboarding cost.
        const dest = tenantSubmitValues(destState);
        body.destTenantId = dest.tenantId;
        if (dest.domain) body.destTenantDomain = dest.domain;
      }
      if (autopilotTag.trim()) body.autopilotGroupTag = autopilotTag.trim();

      const { migrationId } = await createMigration(body);
      // From here the campaign EXISTS. Record it before anything else can throw.
      setCreatedMigrationId(migrationId);

      if (serials.length > 0) {
        await addDevices(migrationId, serials);
      }
      return migrationId;
    },
    onSuccess: (migrationId) => {
      queryClient.invalidateQueries({ queryKey: ['migrations'] });
      queryClient.invalidateQueries({ queryKey: ['migration-groups'] });
      navigate(`/migrations/${encodeURIComponent(migrationId)}/overview`);
    },
  });

  const isLast = step === STEPS.length - 1;

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/migrations"
        className="inline-flex items-center gap-1.5 self-start text-sm no-underline hover:underline"
        style={{ color: ACCENT.hover }}
      >
        <ArrowLeft size={14} strokeWidth={1.5} />
        All campaigns
      </Link>

      <header>
        <h1
          className="font-display text-2xl font-semibold tracking-tight"
          style={{ color: TEXT.primary }}
        >
          New campaign
        </h1>
        <p className="mt-1 font-display text-sm" style={{ color: TEXT.secondary }}>
          A few questions about the devices you are moving. Nothing here starts a
          migration — devices only begin when a wave is activated.
        </p>
      </header>

      {mutation.isError && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>
              {createdMigrationId ? 'Campaign created — devices not assigned' : 'Could not create the campaign'}
            </MessageBarTitle>
            {createdMigrationId ? (
              <>
                The campaign was created, but assigning devices failed:{' '}
                {formatError(mutation.error)}. Open the campaign and add them from its
                Devices tab — nothing was lost.
              </>
            ) : (
              formatCreateError(mutation.error)
            )}
          </MessageBarBody>
          <MessageBarActions>
            {createdMigrationId ? (
              <Button
                size="small"
                onClick={() => navigate(`/migrations/${encodeURIComponent(createdMigrationId)}/overview`)}
              >
                Open campaign
              </Button>
            ) : createMigrationErrorCode(mutation.error) === DESTINATION_TENANT_REQUIRED ? (
              <Button
                size="small"
                onClick={() => {
                  mutation.reset();
                  setStep(2);
                }}
              >
                Choose destination
              </Button>
            ) : (
              <Button size="small" appearance="subtle" onClick={() => mutation.reset()}>
                Dismiss
              </Button>
            )}
          </MessageBarActions>
        </MessageBar>
      )}

      <div
        className="grid min-h-[420px] grid-cols-[200px_1fr]"
        style={{ border: `1px solid ${STROKE.base}`, backgroundColor: SURFACE.base }}
      >
        <ol
          className="flex flex-col gap-0.5 py-2"
          aria-label="Steps"
          style={{ borderRight: `1px solid ${STROKE.base}`, backgroundColor: SURFACE.rowFill }}
        >
          {STEPS.map((label, index) => {
            const isCurrent = index === step;
            const isDone = index < step;
            return (
              <li key={label}>
                <button
                  type="button"
                  // Forward steps are unreachable until they are valid; back
                  // steps stay clickable so a review-step correction is one
                  // click rather than five Backs.
                  disabled={index > step}
                  onClick={() => setStep(index)}
                  aria-current={isCurrent ? 'step' : undefined}
                  className={`${PLAIN_BUTTON} flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left font-display text-[13px] disabled:cursor-default`}
                  style={{
                    borderLeft: `3px solid ${isCurrent ? ACCENT.base : 'transparent'}`,
                    backgroundColor: isCurrent ? SURFACE.rowFillAlt : 'transparent',
                    color: isCurrent
                      ? TEXT.primary
                      : isDone
                        ? TEXT.primary
                        : TEXT.disabled,
                    fontWeight: isCurrent ? 600 : 400,
                  }}
                >
                  <span
                    aria-hidden="true"
                    className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full font-mono text-[11px] tabular-nums"
                    style={{
                      border: `1px solid ${isDone ? STATUS.success : isCurrent ? ACCENT.base : STROKE.base}`,
                      backgroundColor: isDone ? STATUS.successTint : SURFACE.base,
                      color: isDone ? STATUS.success : isCurrent ? ACCENT.pressed : TEXT.disabled,
                    }}
                  >
                    {isDone ? <Check size={12} strokeWidth={2} /> : index + 1}
                  </span>
                  {label}
                </button>
              </li>
            );
          })}
        </ol>

        {/* min-w-0 so wide children (message bars, mono routes) wrap inside the
            grid column instead of forcing the page to scroll horizontally. */}
        <div className="min-w-0 p-5">
          {step === 0 && (
            <>
              <StepHeading
                title="Where do these devices live today?"
                subtitle="Pick the situation that matches the PCs you are moving — that determines the migration path."
              />
              <div
                role="radiogroup"
                aria-label="Where do these devices live today?"
                className="flex flex-col gap-2"
              >
                {DEVICE_HOME_OPTIONS.map((option) => (
                  <OptionCard
                    key={option.value}
                    selected={deviceHome === option.value}
                    icon={option.icon}
                    title={option.title}
                    detail={option.detail}
                    onSelect={() => setDeviceHome(option.value)}
                  />
                ))}
              </div>
              {typeLabel && (
                <div
                  className="mt-4 flex items-center gap-2 pt-3 font-display text-xs"
                  style={{ borderTop: `1px solid ${STROKE.subtle}`, color: TEXT.secondary }}
                >
                  <TypeChip label={typeLabel} />
                  {migrationTypeDescription(migrationTypeWire(typeLabel))}
                </div>
              )}
            </>
          )}

          {step === 1 && (
            <>
              <StepHeading
                title="Name the campaign"
                subtitle="A name your team will recognize, and how big a bite to take first."
              />
              <FieldLabel htmlFor="campaign-name">Campaign name</FieldLabel>
              <Input
                id="campaign-name"
                value={name}
                onChange={(_, data) => setName(data.value)}
                placeholder="e.g. Boston Pilot"
                className="w-full max-w-[360px]"
              />
              <div className="mt-5">
                <div
                  className="mb-1.5 font-display text-[13px] font-medium"
                  style={{ color: TEXT.primary }}
                >
                  Rollout stage
                </div>
                <div role="radiogroup" aria-label="Rollout stage" className="flex flex-col gap-2">
                  {/* Rendered from MIGRATION_WAVES so the console can never
                      offer a value the server would 400 (FD-279). */}
                  {MIGRATION_WAVES.map((value) => (
                    <OptionCard
                      key={value}
                      selected={wave === value}
                      title={value}
                      detail={
                        value === 'Pilot'
                          ? 'A small validation ring — prove the path before the fleet'
                          : 'The real fleet — run at full scale'
                      }
                      onSelect={() => setWave(value)}
                    />
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <StepHeading
                title="Where are they going?"
                subtitle={
                  isCloud
                    ? 'The organization the devices leave, and the one they join.'
                    : hybridDestRequired
                      ? 'Name the Entra tenant these devices will join.'
                      : 'Hybrid campaigns land in this deployment’s own Entra tenant unless you choose otherwise.'
                }
              />
              <div className="flex max-w-[440px] flex-col gap-4">
                {isCloud && (
                  <div>
                    <TenantResolveField
                      label="Source tenant"
                      value={sourceInput}
                      onChange={setSourceInput}
                      state={sourceState}
                    />
                    <TenantReadinessPanel role="source" tenantId={sourceTenantId} />
                  </div>
                )}

                {isCloud ? (
                  <div>
                    <TenantResolveField
                      label="Destination tenant"
                      value={destInput}
                      onChange={setDestInput}
                      state={destState}
                    />
                    <TenantReadinessPanel role="destination" tenantId={destTenantId} />
                  </div>
                ) : hybridDestRequired ? (
                  <div>
                    <TenantResolveField
                      label="Destination tenant"
                      value={destInput}
                      onChange={setDestInput}
                      state={destState}
                    />
                    <p
                      className="mt-2 max-w-[420px] font-display text-xs leading-relaxed"
                      style={{ color: TEXT.secondary }}
                    >
                      {destinationPolicy === 'explicit-hosted'
                        ? 'This environment is hosted for you, so it has no default destination. Enter the domain or tenant ID of the organization these devices will join.'
                        : 'This environment’s hosting has not been confirmed, so no default destination is assumed. Enter the domain or tenant ID of the organization these devices will join.'}{' '}
                      It needs admin consent and a BPRT minting service account before the wave
                      can be activated.
                    </p>
                    <TenantReadinessPanel role="destination" tenantId={destTenantId} />
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    <div
                      className="flex items-start gap-2.5 px-3 py-2.5"
                      style={{
                        border: `1px solid ${STROKE.base}`,
                        backgroundColor: SURFACE.rowFill,
                      }}
                    >
                      <Building2
                        size={16}
                        strokeWidth={1.5}
                        className="mt-0.5 shrink-0"
                        style={{ color: TEXT.secondary }}
                      />
                      <div>
                        <div
                          className="font-display text-[13px] font-medium"
                          style={{ color: TEXT.primary }}
                        >
                          This deployment’s own Entra tenant
                        </div>
                        <div className="font-display text-xs" style={{ color: TEXT.secondary }}>
                          The default, and the path that needs no extra setup.
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setDestOverridden((v) => !v)}
                      aria-expanded={destOverridden}
                      className={`${PLAIN_BUTTON} inline-flex cursor-pointer items-center gap-1 self-start font-display text-[13px] hover:underline`}
                      style={{ color: ACCENT.hover }}
                    >
                      {destOverridden ? (
                        <ChevronDown size={14} strokeWidth={1.5} />
                      ) : (
                        <ChevronRight size={14} strokeWidth={1.5} />
                      )}
                      Send devices to a different tenant instead
                    </button>
                    {destOverridden && (
                      <div>
                        {/* `required` is deliberately FALSE here. FD-254
                            hardcoded it true on this component, and the instant
                            Hybrid gained an OPTIONAL destination the form
                            silently stopped submitting — a `required` Fluent
                            Field fails native validation while empty, so the
                            button looked enabled, the click landed, and nothing
                            happened (FD-253/FD-260). */}
                        <TenantResolveField
                          label="Destination tenant"
                          value={destInput}
                          onChange={setDestInput}
                          state={destState}
                          required={false}
                        />
                        <p
                          className="mt-2 max-w-[420px] font-display text-xs leading-relaxed"
                          style={{ color: TEXT.secondary }}
                        >
                          A different tenant needs admin consent and a BPRT minting service
                          account before the wave can be activated — this deployment’s own
                          tenant needs neither.
                        </p>
                        <TenantReadinessPanel role="destination" tenantId={destTenantId} />
                      </div>
                    )}
                  </div>
                )}
              </div>
              <RouteSummary
                isCloud={isCloud}
                source={sourceInput}
                dest={destInput}
                destOverridden={destOverridden}
                destRequired={hybridDestRequired}
              />
            </>
          )}

          {step === 3 && (
            <>
              <StepHeading
                title="Organize it"
                subtitle="A migration group is a folder for reporting. Groups never change how a migration runs."
              />
              <FieldLabel htmlFor="migration-group">Migration group</FieldLabel>
              <Dropdown
                id="migration-group"
                aria-label="Migration group"
                value={groupChoice === 'new' ? 'Create a new group' : (selectedGroup?.name ?? '')}
                selectedOptions={[groupChoice]}
                onOptionSelect={(_, data) => setGroupChoice((data.optionValue as string) ?? 'new')}
                className="w-full max-w-[360px]"
              >
                <Option value="new" text="Create a new group">
                  Create a new group
                </Option>
                {groups.map((group) => (
                  <Option
                    key={group.migrationGroupId}
                    value={group.migrationGroupId}
                    text={group.name}
                  >
                    {group.name}
                  </Option>
                ))}
              </Dropdown>
              {groupChoice === 'new' && (
                <div className="mt-4">
                  <FieldLabel htmlFor="new-group-name">New group name</FieldLabel>
                  <Input
                    id="new-group-name"
                    value={newGroupName}
                    onChange={(_, data) => {
                      setGroupNameTouched(true);
                      setNewGroupName(data.value);
                    }}
                    className="w-full max-w-[360px]"
                  />
                  <p className="mt-1 font-display text-xs" style={{ color: TEXT.secondary }}>
                    Suggested from the campaign name — rename it, or pick an existing group above.
                  </p>
                </div>
              )}

              <div className="mt-6 pt-3" style={{ borderTop: `1px solid ${STROKE.subtle}` }}>
                <button
                  type="button"
                  onClick={() => setAdvancedOpen((v) => !v)}
                  aria-expanded={advancedOpen}
                  className={`${PLAIN_BUTTON} inline-flex cursor-pointer items-center gap-1 font-display text-[13px] hover:underline`}
                  style={{ color: ACCENT.hover }}
                >
                  {advancedOpen ? (
                    <ChevronDown size={14} strokeWidth={1.5} />
                  ) : (
                    <ChevronRight size={14} strokeWidth={1.5} />
                  )}
                  Advanced
                </button>
                {advancedOpen && (
                  <div className="mt-3">
                    <FieldLabel htmlFor="autopilot-tag">
                      Autopilot group tag (optional)
                    </FieldLabel>
                    <Input
                      id="autopilot-tag"
                      value={autopilotTag}
                      onChange={(_, data) => setAutopilotTag(data.value)}
                      placeholder="e.g. Finance-Laptops"
                      className="w-full max-w-[360px]"
                    />
                    {/* Source-wins, per FD-236 / OQ-011. The campaign tag is the
                        DEFAULT for untagged devices — a device arriving with a
                        tag keeps it. FD-270 is the precedent for why this
                        sentence is worth getting right: the old, inverted
                        wording survived on this very form for three weeks after
                        the decision changed. */}
                    <p
                      className="mt-1 max-w-[420px] font-display text-xs leading-relaxed"
                      style={{ color: TEXT.secondary }}
                    >
                      Applied at destination enrollment. This is the default for devices that
                      arrive with no tag — a device that already has one keeps it.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <StepHeading
                title="Add devices now? (optional)"
                subtitle="Paste serial numbers to assign devices straight away, or skip — devices can always be assigned after the campaign exists."
              />
              <Textarea
                value={serialsText}
                onChange={(_, data) => setSerialsText(data.value)}
                placeholder={'5CD1240XKZ\nPF3T8N2M\nMJ0C7W4Q'}
                aria-label="Device serial numbers"
                resize="vertical"
                className="w-full max-w-[420px]"
                rows={5}
              />
              <p className="mt-1 font-display text-xs" style={{ color: TEXT.secondary }}>
                {serials.length > 0 ? (
                  <>
                    <span className="font-mono tabular-nums">{serials.length}</span> device
                    {serials.length === 1 ? '' : 's'} ready to assign
                  </>
                ) : (
                  'Separate serials with spaces, commas, or new lines.'
                )}
              </p>
              {/* Not a limitation worth hiding: the source-group endpoints are
                  migration-scoped, so browsing them genuinely requires the
                  campaign to exist first. */}
              <MessageBar intent="info" className="mt-4 max-w-[480px]">
                <MessageBarBody>
                  Browsing device groups from the source tenant happens on the campaign’s
                  Devices tab — the lookup is scoped to the campaign’s own tenant, so it
                  needs the campaign to exist first.
                </MessageBarBody>
              </MessageBar>
            </>
          )}

          {step === 5 && typeLabel && (
            <>
              <StepHeading
                title="Review & create"
                subtitle="One last look before the campaign is created."
              />
              <div
                className="px-3"
                style={{ border: `1px solid ${STROKE.base}`, backgroundColor: SURFACE.base }}
              >
                <ReviewRow label="Migration type" value={<TypeChip label={typeLabel} />} />
                <ReviewRow label="Campaign" value={name.trim() || '—'} />
                <ReviewRow label="Rollout stage" value={wave} />
                <ReviewRow
                  label="Route"
                  value={
                    <span className="inline-flex flex-wrap items-center gap-1.5 font-mono text-xs">
                      {isCloud ? sourceInput.trim() : 'on-prem AD'}
                      <RouteFlight />
                      {destInput.trim() || (hybridDestRequired ? '…' : 'this deployment’s tenant')}
                    </span>
                  }
                />
                <ReviewRow
                  label="Group"
                  value={
                    groupChoice === 'new' ? (
                      <>
                        {newGroupName.trim()}{' '}
                        <span style={{ color: TEXT.secondary }}>(new group)</span>
                      </>
                    ) : (
                      (selectedGroup?.name ?? '—')
                    )
                  }
                />
                {autopilotTag.trim() && (
                  <ReviewRow
                    label="Autopilot tag"
                    value={<span className="font-mono text-xs">{autopilotTag.trim()}</span>}
                  />
                )}
                <ReviewRow
                  label="Devices"
                  value={
                    serials.length > 0 ? (
                      <span className="text-xs">
                        <span className="font-mono tabular-nums">{serials.length}</span> to
                        assign on creation
                      </span>
                    ) : (
                      <span className="text-xs" style={{ color: TEXT.secondary }}>
                        None yet — assign after creation
                      </span>
                    )
                  }
                />
              </div>
              {isCloud && (
                <MessageBar intent="info" className="mt-4 max-w-[520px]">
                  <MessageBarBody>
                    Cloud campaigns need identity mappings — who each person becomes in the
                    destination tenant — before the wave can be activated. Set them up on the
                    campaign’s Identities tab.
                  </MessageBarBody>
                </MessageBar>
              )}
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {step > 0 && (
          <Button appearance="secondary" onClick={() => setStep((s) => s - 1)}>
            Back
          </Button>
        )}
        {isLast ? (
          <Button
            appearance="primary"
            disabled={
              !stepValid || !routeValid || mutation.isPending || createdMigrationId !== null
            }
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Creating…' : 'Create migration'}
          </Button>
        ) : (
          <Button appearance="primary" disabled={!stepValid} onClick={() => setStep((s) => s + 1)}>
            Next
          </Button>
        )}
        <Button appearance="subtle" onClick={() => navigate('/migrations')}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ——— step chrome ————————————————————————————————————————————————————————

function StepHeading({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="pb-4">
      <h2
        className="font-display text-base font-semibold tracking-tight"
        style={{ color: TEXT.primary }}
      >
        {title}
      </h2>
      <p className="mt-1 font-display text-[13px]" style={{ color: TEXT.secondary }}>
        {subtitle}
      </p>
    </div>
  );
}

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-1.5 block font-display text-[13px] font-medium"
      style={{ color: TEXT.primary }}
    >
      {children}
    </label>
  );
}

function OptionCard({
  selected,
  icon,
  title,
  detail,
  onSelect,
}: {
  selected: boolean;
  icon?: ReactNode;
  title: string;
  detail: string;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={`${PLAIN_BUTTON} flex w-full cursor-pointer items-start gap-3 px-3 py-2.5 text-left transition-colors`}
      style={{
        border: `1px solid ${selected ? ACCENT.base : STROKE.base}`,
        backgroundColor: selected ? ACCENT.tint : SURFACE.base,
      }}
    >
      {icon && (
        <span className="mt-0.5 shrink-0" style={{ color: selected ? ACCENT.pressed : TEXT.secondary }}>
          {icon}
        </span>
      )}
      <span className="min-w-0">
        <span className="block font-display text-[13px] font-medium" style={{ color: TEXT.primary }}>
          {title}
        </span>
        <span className="block font-display text-xs" style={{ color: TEXT.secondary }}>
          {detail}
        </span>
      </span>
      {selected && (
        <Check
          size={16}
          strokeWidth={1.5}
          className="ml-auto shrink-0"
          style={{ color: ACCENT.pressed }}
        />
      )}
    </button>
  );
}

function TypeChip({ label }: { label: MigrationTypeLabel }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 font-display text-[11px] font-semibold"
      style={{
        border: `1px solid ${ACCENT.base}`,
        backgroundColor: ACCENT.tint,
        color: ACCENT.pressed,
      }}
    >
      {label === 'Cloud' ? (
        <Cloud size={12} strokeWidth={1.5} />
      ) : (
        <Network size={12} strokeWidth={1.5} />
      )}
      {label}
    </span>
  );
}

function RouteSummary({
  isCloud,
  source,
  dest,
  destOverridden,
  destRequired,
}: {
  isCloud: boolean;
  source: string;
  dest: string;
  destOverridden: boolean;
  /** FD-594 — no default destination exists, so an empty field must not name one. */
  destRequired: boolean;
}) {
  const destLabel = isCloud || destRequired
    ? dest.trim() || '…'
    : destOverridden && dest.trim()
      ? dest.trim()
      : 'this deployment’s tenant';
  return (
    <div
      className="mt-5 flex flex-wrap items-center gap-1.5 pt-3 font-mono text-xs"
      style={{ borderTop: `1px solid ${STROKE.subtle}`, color: TEXT.secondary }}
    >
      {isCloud ? source.trim() || '…' : 'on-prem AD'}
      <RouteFlight />
      {destLabel}
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div
      className="grid grid-cols-[160px_1fr] gap-3 py-2.5 [&:not(:first-child)]:border-t"
      style={{ borderColor: STROKE.subtle }}
    >
      <span
        className="font-display text-[11px] font-medium uppercase tracking-[0.06em]"
        style={{ color: TEXT.secondary }}
      >
        {label}
      </span>
      <span className="font-display text-[13px]" style={{ color: TEXT.primary }}>
        {value}
      </span>
    </div>
  );
}

// ——— tenant-slot helpers ————————————————————————————————————————————————

/** A slot is submittable once it holds a usable tenant ID. */
function tenantSlotReady(state: TenantResolveState): boolean {
  return state.kind === 'guid' || state.kind === 'resolved';
}

/** The settled GUID for a slot, else null — drives the consent probe. */
function settledTenantId(state: TenantResolveState): string | null {
  if (state.kind === 'resolved') return state.data.tenantId;
  if (state.kind === 'guid') return state.tenantId;
  return null;
}

/** The tenant ID plus the domain LABEL to submit (FD-110 passthrough). */
function tenantSubmitValues(state: TenantResolveState): { tenantId: string; domain?: string } {
  if (state.kind === 'resolved') {
    return { tenantId: state.data.tenantId, domain: state.data.domain };
  }
  if (state.kind === 'guid') return { tenantId: state.tenantId };
  return { tenantId: '' };
}

/**
 * The create-chain error, with the one code an operator can act on in this
 * wizard spelled out (FD-594). Every other failure keeps `formatError`.
 */
function formatCreateError(err: unknown): string {
  if (createMigrationErrorCode(err) === DESTINATION_TENANT_REQUIRED) {
    return 'This environment has no default destination tenant, so a Hybrid campaign must name the Entra tenant its devices will join. Choose the destination and create the campaign again.';
  }
  return formatError(err);
}

function formatError(err: unknown): string {
  if (err instanceof ApiError) {
    return err.message || `Request failed (${err.status}).`;
  }
  if (err instanceof Error) return err.message;
  return 'An unknown error occurred.';
}
